export type DynamicTranslationCacheItem = {
    value: string;
    expiry: number;
    timestamp: number;
    version: string;
};

/**
 * This service is used to cache dynamic translations in memory and localStorage.
 * It is used to avoid unnecessary API calls to the server.
 * It is also used to avoid unnecessary re-rendering of the component when the translation is not available.
 */
class DynamicTranslationCacheService {
    /**
     * The time to live for the cache in milliseconds.
     */
    private ttl: number;

    /**
     * The memory cache.
     */
    private memoryCache: Map<string, DynamicTranslationCacheItem>;

    /**
     * The maximum number of items to keep in memory.
     */
    private maxMemoryItems: number;

    /**
     * The prefix for the storage key.
     */
    private storageKeyPrefix: string;

    /**
     * The maximum number of items to keep in localStorage.
     */
    private maxLocalStorageItems: number;

    /**
     * The version of the cache. Used to invalidate the cache when the version changes
     * (new app deployments, etc).
     */
    private cacheVersion: string;

    /**
     * Whether logging is enabled.
     */
    private loggingEnabled: boolean;

    constructor({
        ttl = 24 * 60 * 60 * 1000,
        maxMemoryItems = 10,
        storageKeyPrefix = 'idt_i18n_dt_',
        maxLocalStorageItems = 100,
        cacheVersion = 'v1',
        loggingEnabled = false,
    }: {
        ttl?: number;
        maxMemoryItems?: number;
        storageKeyPrefix?: string;
        maxLocalStorageItems?: number;
        cacheVersion?: string;
        loggingEnabled?: boolean;
    } = {}) {
        if (loggingEnabled) {
            console.group('[idt-i18n::DynamicTranslationCacheService]');
            console.log('TTL', ttl);
            console.log('Max memory items', maxMemoryItems);
            console.log('Storage key prefix', storageKeyPrefix);
            console.log('Cache version', cacheVersion);
            console.log('Logging enabled', loggingEnabled);
            console.groupEnd();
        }

        this.memoryCache = new Map();
        this.ttl = ttl;
        this.maxMemoryItems = maxMemoryItems;
        this.storageKeyPrefix = storageKeyPrefix;
        this.maxLocalStorageItems = maxLocalStorageItems;
        this.cacheVersion = cacheVersion;
        this.loggingEnabled = loggingEnabled;
    }

    /**
     * Generate a cache key for the given key.
     * @param key - The key to generate a cache key for.
     * @returns The cache key.
     */
    private generateKey(key: string): string {
        return `${this.storageKeyPrefix}${key}`;
    }

    /**
     * Check if the item is valid.
     * @param item - The item to check.
     * @returns True if the item is valid, false otherwise.
     */
    private isItemValid(item: DynamicTranslationCacheItem): boolean {
        return item.expiry > Date.now() && item.version === this.cacheVersion;
    }

    /**
     * Get a value from the cache.
     * @param key - The key to get the value for.
     * @returns The value, or null if the value is not found or is expired.
     */
    get(key: string): string | null {
        const cacheKey = this.generateKey(key);

        if (this.loggingEnabled) {
            console.log('[idt-i18n::DynamicTranslationCacheService] Getting value from cache:', key);
        }

        // Check memory cache first
        if (this.memoryCache.has(cacheKey)) {
            const inMemoryItem = this.memoryCache.get(cacheKey);

            if (inMemoryItem && this.isItemValid(inMemoryItem)) {
                if (this.loggingEnabled) {
                    console.log('[idt-i18n::DynamicTranslationCacheService] Cache hit in memory:', key);
                }

                return inMemoryItem.value;
            }

            // Cache miss, remove item
            this.memoryCache.delete(cacheKey);
        }

        // Check localStorage
        try {
            const rawStoredItem = localStorage.getItem(cacheKey);

            if (rawStoredItem) {
                const storedItem = JSON.parse(rawStoredItem);

                if (storedItem && this.isItemValid(storedItem)) {
                    // Add back to memory cache - Refresh memory stack
                    this.memoryCache.set(cacheKey, storedItem);

                    if (this.loggingEnabled) {
                        console.log('[idt-i18n::DynamicTranslationCacheService] Cache hit in localStorage:', key);
                    }

                    return storedItem.value;
                }

                // Cache miss, remove item
                localStorage.removeItem(cacheKey);
            }
        } catch (error) {
            console.warn('Error reading from localStorage:', error);
        }

        return null;
    }

    /**
     * Set a value in the cache.
     * @param key - The key to set the value for.
     * @param value - The value to set.
     */
    set(key: string, value: string): void {
        const cacheKey = this.generateKey(key);
        const timestamp = Date.now();
        const expiry = timestamp + this.ttl;

        const item: DynamicTranslationCacheItem = {
            value,
            expiry,
            timestamp,
            version: this.cacheVersion,
        };

        if (this.loggingEnabled) {
            console.log('[idt-i18n::DynamicTranslationCacheService] Setting value in cache:', key);
        }

        // Add to memory cache
        this.memoryCache.set(cacheKey, item);

        // Cleanup memory cache if too large
        if (this.memoryCache.size > this.maxMemoryItems) {
            if (this.loggingEnabled) {
                console.log('[idt-i18n::DynamicTranslationCacheService] Memory cache is full, deleting oldest item');
            }

            const oldestKey = this.memoryCache.keys().next().value;

            this.memoryCache.delete(oldestKey);
        }

        // Add to localStorage
        try {
            const storageKeys = Object.keys(localStorage);
            const oldestKeys = storageKeys.filter((k) => k.startsWith(this.storageKeyPrefix)).sort();

            if (oldestKeys.length >= this.maxLocalStorageItems) {
                if (this.loggingEnabled) {
                    console.log('[idt-i18n::DynamicTranslationCacheService] localStorage is full, deleting oldest item');
                }

                localStorage.removeItem(oldestKeys[0]);
            }

            localStorage.setItem(cacheKey, JSON.stringify(item));
        } catch (error) {
            console.warn('Error writing to localStorage:', error);
        }
    }

    /**
     * Clear the cache.
     */
    clear(fullClear = false): void {
        if (this.loggingEnabled) {
            console.log('[idt-i18n::DynamicTranslationCacheService] Clearing cache');
        }

        this.memoryCache.clear();

        if (fullClear) {
            if (this.loggingEnabled) {
                console.log('[idt-i18n::DynamicTranslationCacheService] Clearing localStorage');
            }

            try {
                const keys = Object.keys(localStorage);
                keys.forEach((key) => {
                    if (key.startsWith(this.storageKeyPrefix)) {
                        localStorage.removeItem(key);
                    }
                });
            } catch (error) {
                console.warn('Error clearing localStorage:', error);
            }
        }
    }
}

export default DynamicTranslationCacheService;
